import { useCallback, useEffect, useMemo, useState } from 'react';
import { createContainer } from 'unstated-next';
import { useLocation, useNavigate } from 'react-router-dom';
import { first, isEmpty } from 'lodash';
import { useMutation, useQuery } from '@tanstack/react-query';
import { usePaSurveyParams } from '../../../../report/use-pa-survey-params';
import { getPatientBasePath } from '../../../../app/route-utils';
import {
  AdhocQuestion,
  retrieveAdhocMessages,
  updateAdhocMessages,
} from '../../../api/patient-adhoc';

function useV2PaAdhocContainer() {
  const navigate = useNavigate();
  const location = useLocation();
  const { orgId, shortId, adhocId } = usePaSurveyParams();
  const patientBasePath = getPatientBasePath(location.pathname);

  const [adhocState, setAdhocState] = useState<AdhocQuestion[]>([]);
  const [visitId, setVisitId] = useState<number | null>(null);

  const finishedPath = useMemo(
    () => `${patientBasePath}/${orgId}/${shortId}/adhoc/${adhocId}/finished`,
    [adhocId, orgId, patientBasePath, shortId],
  );

  const {
    data: retrieveData,
    isLoading: isRetrieveLoading,
    isFetching: isRetrieveFetching,
    isError: isRetrieveError,
    error: retrieveError,
  } = useQuery({
    queryKey: ['patient-adhoc', adhocId],
    enabled: Boolean(adhocId),
    retry: false,
    queryFn: async () => retrieveAdhocMessages(adhocId || ''),
  });

  useEffect(() => {
    if (!retrieveData) return;
    setAdhocState(retrieveData.adHocQuestions || []);
    setVisitId(retrieveData.visitId || null);
  }, [retrieveData]);

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!visitId) {
        throw new Error('visitId is missing');
      }
      return updateAdhocMessages(visitId, {
        adHocQuestions: adhocState.map((item) => ({
          uniqueId: item.uniqueId,
          answer: item.answer || '',
        })),
      });
    },
    onSuccess: () => {
      navigate(finishedPath);
    },
  });

  const hasQuestions = adhocState.length > 0;
  const isValid =
    hasQuestions &&
    adhocState.every((question) => !isEmpty(question.answer));

  const updateAnswer = useCallback(
    ({
      questionKey,
      answerValues,
    }: {
      questionKey: string;
      answerValues?: string[] | null;
    }) => {
      const uniqueId = questionKey.replace('q-adhoc-text-', '');
      const answer = first(answerValues) || '';

      setAdhocState((prev) =>
        prev.map((question) =>
          question.uniqueId === uniqueId
            ? {
                ...question,
                answer,
              }
            : question,
        ),
      );
    },
    [],
  );

  const handleSave = useCallback(() => {
    if (!isValid || updateMutation.isPending) {
      return;
    }
    updateMutation.mutate();
  }, [isValid, updateMutation]);

  return {
    params: {
      orgId,
      shortId,
      adhocId,
    },
    adhocState,
    updateAnswer,
    handleSave,
    hasQuestions,
    isValid,
    isLoading: isRetrieveLoading || isRetrieveFetching,
    isSaving: updateMutation.isPending,
    isError: isRetrieveError || updateMutation.isError,
    errorMessage:
      (retrieveError as Error | null)?.message ||
      (updateMutation.error as Error | null)?.message ||
      '',
  };
}

const V2PaAdhocContainer = createContainer(useV2PaAdhocContainer);

export default V2PaAdhocContainer;
